'use client'

import { Cell, Icon } from '@vinted/web-ui'
import { Block24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { reloadPage } from '@marketplace-web/shared/browser-util'
import {
  ConversationActionModal,
  OppositeUser,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  blockUser,
  unblockUser,
} from '@marketplace-web/conversation-safety/conversation-safety-data'

import UserBlockModal from '../UserBlockModal'

type Props = {
  oppositeUser?: OppositeUser | null
  userId?: number
  isBrazeConversationCard: boolean
  visibleModalName: ConversationActionModal | null
  onSetVisibleModalName: (modalName: ConversationActionModal | null) => void
}

const UserBlockAction = ({
  oppositeUser,
  userId,
  isBrazeConversationCard,
  visibleModalName,
  onSetVisibleModalName,
}: Props) => {
  const baseTranslate = useTranslate()

  const isBlockingImprovementsEnabled = useFeatureSwitch('conversation_blocking_improvements')

  const handleModalUserBlock = () => {
    onSetVisibleModalName(null)
    reloadPage()
  }

  const handleBlockUserClick = async () => {
    if (!oppositeUser || !userId) return

    if (isBlockingImprovementsEnabled) {
      onSetVisibleModalName(ConversationActionModal.BlockUser)

      return
    }

    const blockFunction = oppositeUser.isHated ? unblockUser : blockUser

    await blockFunction({ currentUserId: userId, blockedUserId: Number(oppositeUser.id) })

    reloadPage()
  }

  const handleCancelClick = () => {
    onSetVisibleModalName(null)
  }

  if (!oppositeUser || oppositeUser.isUserUnblockable || isBrazeConversationCard) return null

  const actionKey = oppositeUser.isHated ? 'unblock' : 'block'

  return (
    <>
      <Cell
        title={baseTranslate(`user.admin.actions.${actionKey}`)}
        prefix={<Icon name={Block24} color={Icon.Color.Warning} />}
        type={Cell.Type.Navigating}
        onClick={handleBlockUserClick}
        testId={`conversation-actions-${actionKey}`}
      />
      <UserBlockModal
        userId={Number(oppositeUser.id)}
        username={oppositeUser.login}
        isUserBlocked={oppositeUser.isHated}
        isVisible={visibleModalName === ConversationActionModal.BlockUser}
        onCancel={handleCancelClick}
        onConfirm={handleModalUserBlock}
      />
    </>
  )
}

export default UserBlockAction
